// accets/report/ReportPage.js
import { useState, useEffect, useCallback } from "react";
import "./ReportPage.css";

import IconOpen from "./icon-open.png";
import IconClose from "./icon-close.png";

import { fetchReportExcel, fetchReports, fetchStreams, fetchTrackers } from "../../services/requests";
import { passiveTeamsLast } from "../../services/passiveTeamsLast";
import { useGetUserInfo } from "../../services/util";

import Header from "../header/header";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

export default function ReportPage({ defaultIsActive = true }) {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [trackers, setTrackers] = useState([]);
  const [streams, setStreams] = useState([]);
  const [page] = useState(0);
  const [size] = useState(10000);
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');

  // фильтры
  const [isActive, setIsActive] = useState(defaultIsActive);
  const [trackerFilterOpen, setTrackerFilterOpen] = useState(false);
  const [streamFilterOpen, setStreamFilterOpen] = useState(false);
  const [filterTrackers, _setFilterTrackers] = useState(null);
  const [filterStreams, _setFilterStreams] = useState(null);
  const [trackerSearchQuery, setTrackerSearchQuery] = useState('');
  const setFilterTrackers = (newTracker) => {
    _setFilterTrackers(newTracker);
    setTrackerSearchQuery("");
    setTrackerFilterOpen(false);
  }
  const setFilterStreams = (newStreams) => {
    _setFilterStreams(newStreams);
    setStreamFilterOpen(false);
  }

  // Определение активного потока
  const isStreamActive = (stream) => {
    const todayDate = new Date().toISOString().split('T')[0];
    return stream.startDate <= todayDate && stream.endDate >= todayDate;
  };

  // Сортировка потоков в фильтре
  const getSortedStreams = (streamsToSort) => {
    const activeStreams = streamsToSort.filter(isStreamActive);
    const inactiveStreams = streamsToSort.filter(s => !isStreamActive(s));

    activeStreams.sort((a, b) => {
      if (a.endDate !== b.endDate) {
        return new Date(b.endDate) - new Date(a.endDate);
      }
      return a.name.localeCompare(b.name);
    });

    inactiveStreams.sort((a, b) => {
      if (a.endDate !== b.endDate) {
        return new Date(b.endDate) - new Date(a.endDate);
      }
      return a.name.localeCompare(b.name);
    });

    return [...activeStreams, ...inactiveStreams];
  };

  // Сортировка отчётов в таблице
  const getSortedReports = (reportsToSort) => {
    const todayDate = new Date().toISOString().split('T')[0];
    let sorted = [...reportsToSort];

    sorted.sort((a, b) => {
      const aActive = a.startDate <= todayDate && a.endDate >= todayDate;
      const bActive = b.startDate <= todayDate && b.endDate >= todayDate;

      if (aActive && !bActive) return -1;
      if (!aActive && bActive) return 1;

      if (aActive && bActive) {
        if (a.endDate !== b.endDate) {
          return new Date(b.endDate) - new Date(a.endDate);
        }
      } else if (!aActive && !bActive) {
        if (a.endDate !== b.endDate) {
          return new Date(b.endDate) - new Date(a.endDate);
        }
      }

        if (a.streamName !== b.streamName) {
          return a.streamName.localeCompare(b.streamName);
        }

        return a.teamCardName.localeCompare(b.teamCardName);
      });

      if (sortColumn) {
        sorted.sort((a, b) => {
          let aVal, bVal;

          if (sortColumn === 'averageTeamGrade') {
            aVal = a.averageTeamGrade === null ? -1 : parseFloat(a.averageTeamGrade);
            bVal = b.averageTeamGrade === null ? -1 : parseFloat(b.averageTeamGrade);
          } else if (sortColumn === 'averageUserGrade') {
            aVal = a.averageUserGrade === null ? -1 : parseFloat(a.averageUserGrade);
            bVal = b.averageUserGrade === null ? -1 : parseFloat(b.averageUserGrade);
          } else if (sortColumn === 'readinessLevel') {
            aVal = a.readinessLevel ? parseInt(a.readinessLevel) : -1;
            bVal = b.readinessLevel ? parseInt(b.readinessLevel) : -1;
          } else if (sortColumn === 'teamCardName') {
            aVal = a.teamCardName || '';
            bVal = b.teamCardName || '';
            if (sortDirection === 'desc') {
              return bVal.localeCompare(aVal);
            } else {
              return aVal.localeCompare(bVal);
            }
          }

          if (sortDirection === 'desc') {
            return bVal - aVal;
          } else {
            return aVal - bVal;
          }
        });
      }

      return passiveTeamsLast(sorted);
    };

  // Обработчик сортировки колонок
  const handleColumnSort = (column) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortColumn(null);
        setSortDirection('desc');
      } else {
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortColumn !== field) {
      return <span className="sort-icon sort-icon-inactive">↕</span>;
    }
    return (
      <span className="sort-icon sort-icon-active">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const getSortLabelForText = (field) => {
    if (sortColumn !== field) {
      return 'А→Я';
    }
    return sortDirection === 'asc' ? 'А→Я' : 'Я→А';
  };
   
  const buildFilters = useCallback(() => {
    const filters = [];
    if (filterTrackers) filters.push({
      fieldName: "username",
      type: "EQ",
      value: filterTrackers,
    });
    if (filterStreams) filters.push({
      fieldName: "streams.name",
      type: "EQ",
      value: filterStreams,
    });
    if (isActive) {
      const todayDate = new Date().toISOString().split('T')[0];
      filters.push({
        fieldName: "streams.startDate",
        type: "LTE",
        value: todayDate,
      }, {
        fieldName: "streams.endDate",
        type: "GTE",
        value: todayDate,
      });
    }
    return filters;
  }, [filterTrackers, filterStreams, isActive]);

  const handleExportExcel = async () => {
    try {
      const response = await fetchReportExcel({ filters: buildFilters() });
      if (!response.ok) throw new Error(`Ошибка HTTP: ${response.status}`);

      const disposition = response.headers.get("Content-Disposition");
      let filename = "отчёт-по-командам.xlsx";
      if (disposition) {
        const match = disposition.match(/filename\*=UTF-8''(.+)/);
        if (match) {
          filename = decodeURIComponent(match[1]);
        }
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 75);
    } catch (error) {
      console.error("Ошибка выгрузки отчёта", error);
    }
  };

  const loadReports = useCallback(async () => {
    try {
      const response = await fetchReports({ page, size, filters: buildFilters() });
      if (!response.ok) {
        throw new Error(`Ошибка HTTP: ${response.status}`);
      }
      const data = await response.json();
      setReports(data.content);
    } catch (error) {
      console.error("Ошибка загрузки отчётов", error);
    }
  // eslint-disable-next-line
  }, [page, size, filterTrackers, filterStreams, isActive]);

  const loadStreams = useCallback(async () => {
    try {
      const response = await fetchStreams({ page: page, size: size });
      if (!response.ok) {
        throw new Error(`Ошибка HTTP: ${response.status}`);
      }
      const data = await response.json();
      setStreams(data.content);
    } catch (error) {
      console.error("Ошибка загрузки отчётов", error);
    }
  }, [page, size]);

  const loadTrackers = useCallback(async () => {
    try {
      const response = await fetchTrackers({ page: page, size: size });
      if (!response.ok) {
        throw new Error(`Ошибка HTTP: ${response.status}`);
      }
      const data = await response.json();
      setTrackers(data.content);
    } catch (error) {
      console.error("Ошибка загрузки отчётов", error);
    }
  }, [page, size]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadReports(), loadStreams(), loadTrackers()]).then(
      () => setLoading(false),
      (error) => {
        console.error("Ошибка загрузки отчётов", error);
        setLoading(false);
      }
    );
  }, [loadReports, loadStreams, loadTrackers]);

  const user = useGetUserInfo();
  useEffect(() => {
    setUserRole(user.roles[0]);
  }, [user]);

  const reportUsernames = reports.map((report) => report.username);
  return (
    <div className="Report">
      <Header userRole={userRole}></Header>

      {/* Контент */}
      <main className="Report-main">
        <div className="report-header">
          {/* Кнопка выгрузки отчета */}
          <button className="report-btn" onClick={handleExportExcel}>
            Выгрузить отчет
          </button>

          {/* фильтры */}
          <div className="report-filters">
            <button
              data-testid="button-isactive"
              className="report-page_btn-isactive"
              onClick={() => setIsActive((prev) => !prev)}
            >
              <input
                id="isActive"
                type="checkbox"
                checked={!isActive}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsActive((prev) => !prev)
                }}
              />
              <label>Показывать неактивные</label>
            </button>
           <div className="report-dropdown1">
  <button
    data-testid="trackers-btn"
    className={`report-dropdown-btn ${trackerFilterOpen ? 'open' : ''}`}
    onClick={() => setTrackerFilterOpen(!trackerFilterOpen)}
  >
    Трекеры
    <span className="report-dropdown-icon1">
  <img
    src={trackerFilterOpen ? IconClose : IconOpen}
    alt={trackerFilterOpen ? "Закрыто" : "Открыто"}
    className="report-dropdown-icon-img"
  />
</span>

  </button>
  {trackerFilterOpen && (
    <div data-testid="trackers-dropdown-menu" className="report-dropdown-menu">
      <input
        type="text"
        placeholder="Поиск по имени или логину..."
        className="report-dropdown-item report-search-input"
        value={trackerSearchQuery}
        onChange={(e) => setTrackerSearchQuery(e.target.value)}
      />
      <button
        key={0}
        className="report-dropdown-item"
        onClick={() => setFilterTrackers(null)}
      >—</button>
      {trackers.filter((tracker) => {
        const query = trackerSearchQuery.toLowerCase();
        return reportUsernames.includes(tracker.username) && (
          tracker.fullName.toLowerCase().includes(query) ||
          tracker.username.toLowerCase().includes(query)
        );
      }).map((t, i) => (
        <button
          key={i}
          className="report-dropdown-item"
          onClick={() => setFilterTrackers(t.username)}
        >{`${t.fullName} (${t.username})`}</button>
      ))}
    </div>
  )}
</div>

<div className="report-dropdown2">
  <button
    className={`report-dropdown-btn ${streamFilterOpen ? 'open' : ''}`}
    onClick={() => setStreamFilterOpen(!streamFilterOpen)}
  >
    Потоки
    <span className="report-dropdown-icon2">
  <img
    src={streamFilterOpen ? IconClose : IconOpen}
    alt={streamFilterOpen ? "Закрыто" : "Открыто"}
    className="report-dropdown-icon-img"
  />
</span>

  </button>
  {streamFilterOpen && (
    <div data-testid="streams-dropdown-menu" className="report-dropdown-menu">
      <button
        key={0}
        className="report-dropdown-item"
        onClick={() => setFilterStreams(null)}
      >—</button>
      {getSortedStreams(streams).map((t, i) => (
        <button
          key={i}
          className={`report-dropdown-item ${isStreamActive(t) ? 'active-stream' : ''}`}
          onClick={() => setFilterStreams(t.name)}
        >
          {isStreamActive(t) && <span className="active-stream-dot"></span>}
          {t.name}
        </button>
      ))}
    </div>
  )}
</div>

          </div>
        </div>

        {/* таблица */}
        <div className="report-table-container">
          <table className="report-table">
            <thead>
              <tr>
                <th>№</th>
                <th>Название потока</th>
                <th>Сроки потока</th>
                <th 
                  className={`sortable ${sortColumn === 'teamCardName' ? 'sorted ' + sortDirection : ''}`}
                  onClick={() => handleColumnSort('teamCardName')}
                >
                  Название команды {getSortLabelForText('teamCardName')}
                </th>
                <th>Имя трекера</th>
                <th 
                  className={`sortable ${sortColumn === 'averageTeamGrade' ? 'sorted ' + sortDirection : ''}`}
                  onClick={() => handleColumnSort('averageTeamGrade')}
                >
                  Средняя оценка команды {getSortIcon('averageTeamGrade')}
                </th>
                <th 
                  className={`sortable ${sortColumn === 'averageUserGrade' ? 'sorted ' + sortDirection : ''}`}
                  onClick={() => handleColumnSort('averageUserGrade')}
                >
                  Средняя оценка трекера {getSortIcon('averageUserGrade')}
                </th>
                <th>Трекшн-митинги (факт/план)</th>
                <th>Рынки НТИ</th>
                <th 
                  className={`sortable ${sortColumn === 'readinessLevel' ? 'sorted ' + sortDirection : ''}`}
                  onClick={() => handleColumnSort('readinessLevel')}
                >
                  Уровень TRL {getSortIcon('readinessLevel')}
                </th>
              </tr>
            </thead>
            <tbody>
  {loading && (
    <tr>
      <td colSpan="10">Загрузка...</td>
    </tr>
  )}

  {!loading && reports.length === 0 && (
    <tr>
      <td colSpan="10">Нет данных</td>
    </tr>
  )}

  {!loading &&
    getSortedReports(reports).map((item, index) => {
      const todayDate = new Date().toISOString().split('T')[0];
      const itemIsActive = item.startDate <= todayDate && item.endDate >= todayDate;
      return (
        <tr key={index} className={!isActive && itemIsActive ? 'active-row' : ''}>
          <td>{index + 1 + page * size}</td>

          <td
            style={{ cursor: 'pointer', color: '#843AEB', textDecoration: 'underline' }}
            onClick={() => navigate(`/report/${item.streamId}`)}
          >
            {item.streamName}
          </td>

          <td>
            {item.startDate} – {item.endDate}
          </td>

          <td
            style={{ cursor: 'pointer', color: '#843AEB', textDecoration: 'underline' }}
            onClick={() => navigate(`/teamcard/${item.teamId}`)}
          >
            {item.teamCardName}{item.passive ? ' (отчислена)' : ''}
          </td>

          <td>{`${trackers?.filter((tracker) => tracker.username === item.username)[0]?.fullName} (${item.username})`}</td>

          <td>{item.averageTeamGrade ?? "—"}</td>

          <td>{item.averageUserGrade ?? "—"}</td>

          <td>
            {item.meetingsCountFact}/{item.meetingsCountPlan}
          </td>

          <td>{item.ntiMarkets.join(", ")}</td>

          <td>{item.readinessLevel}</td>
        </tr>
      );
    })}
</tbody>

          </table>
        </div>
      </main>
    </div>
  );
}

ReportPage.propTypes = {
  defaultIsActive: PropTypes.bool,
};
